import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'

import { PageContainer } from '../../components/layout/PageContainer'
import { BackButton } from '../../components/BackButton'
import { TestIds } from '../../testIds'
import { Loader } from '../../components/Loader'
import { CONTENT_LAYOUT_WIDTH } from '../../util/consts'
import { StatusHeader } from '../../components/StatusHeader'
import StatusChip from '../../components/StatusChip'
import { showQuoteAsTwoColumnLayout, mapQuoteStatus } from './utils/getQuoteStatus'
import { QuoteExpiry } from './components/QuotesList/QuoteExpiry'
import { AcceptQuotePayload, Quote, QuoteStatus, SearchQuotesResponse } from './storage/types'
import { CartColumnView, FlexShrink } from '../../pages/CartComponents'
import QuoteSummary from './components/QuoteDetails/QuoteSummary'
import apiClient from '../../services/api-client'
import { AxiosError } from 'axios'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../azure-b2c/storage'
import YesNoAction from '../../components/YesNoAction'
import { QuoteCreatedAt } from './components/QuotesList/QuoteCreatedAt'
import { CartItem } from '../cart/storage/cart.types'
import { getProductsForQuote } from './utils/getProductsForQuote'
import { cloneDeep, isEqual, pick } from 'lodash'
import { QuoteSummaryTotal } from './components/QuoteDetails/QuoteSummaryTotal'
import { Banner } from '../../components/Banner'
import {
  HandleSubmitQuoteParams,
  QuoteConfirmDetails,
} from './components/QuoteDetails/QuoteConfirmDetails'
import { QuoteDetailsProducts } from './components/QuoteDetails/QuoteDetailsProducts'
import { Button } from '../../components/Button'
import { SendOfferModal } from './components/QuoteDetails/SendOfferModal'
import { QuoteMoreOptions } from './components/QuoteDetails/QuoteMoreOptions'

const QuoteDetails = () => {
  const [loading, setLoading] = useState(true)
  const [quote, setQuote] = useState<Quote | null>(null)
  const [originalQuote, setOriginalQuote] = useState<Quote | null>(null)
  const [products, setProducts] = useState<CartItem[]>([])
  const [originalProducts, setOriginalProducts] = useState<CartItem[]>([])
  const [note, setNote] = useState<string | null>(null)

  const [error, setError] = useState(false)
  const [isNoQuoteFound, setIsNoQuoteFound] = useState(false)
  const [isSubmitQuoteModalOpen, setIsSubmitQuoteModalOpen] = useState(false)
  const [isSendOfferModalOpen, setIsSendOfferModalOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const navigate = useNavigate()
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const { quoteId } = useParams()

  const account = useSelector(selectCurrentFarmerAccount)
  const isTfo = account?.hasTfoAccess || false

  const isQuoteModified = useMemo(() => {
    const productProperties = ['quantity', 'productId', 'editedPriceExcGst']

    const productChattels = products.map((p) => pick(p, productProperties))
    const originalProductChattels = originalProducts.map((p) => pick(p, productProperties))

    const areProductsModified = !isEqual(productChattels, originalProductChattels)

    const isDeliveryModified = !isEqual(quote?.delivery, originalQuote?.delivery)
    const hasNote = note !== null

    return areProductsModified || isDeliveryModified || hasNote
  }, [quote, products, originalProducts, originalQuote])

  useEffect(() => {
    if (isQuoteModified) {
      const handleBeforeUnload = (event: WindowEventMap['beforeunload']) => {
        event.preventDefault()
      }
      window.addEventListener('beforeunload', handleBeforeUnload)

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    }
  }, [isQuoteModified])

  const mappedStatus = React.useMemo(() => {
    if (quote) return mapQuoteStatus(quote.status)
    return null
  }, [quote])

  useEffect(() => {
    const getQuote = async () => {
      try {
        const result = await apiClient.get<Quote, AxiosError>(
          `/quotes/${quoteId}?accountNumber=${account?.accountNumber}`,
        )
        if (result && result.status === 200 && result.data) {
          setQuote(result.data)
          setOriginalQuote(result.data)
          setIsNoQuoteFound(false)
        }
      } catch (error) {
        setLoading(false)
        setIsNoQuoteFound(true)
      }
    }
    getQuote()
  }, [])

  useEffect(() => {
    const getProducts = async () => {
      if (quote && account?.accountNumber) {
        const productResults = await getProductsForQuote(quote, account?.accountNumber)
        setProducts(productResults)
        setOriginalProducts(cloneDeep(productResults))
        setLoading(false)
      }
    }
    getProducts()
  }, [quote?.products, account?.accountNumber])

  const onSend = () => {
    setIsSendOfferModalOpen(true)
  }

  const renderMoreOptions = () => {
    if (quote?.status !== QuoteStatus.Offered) return null
    else return <QuoteMoreOptions quote={quote} />
  }

  const renderTitle = () => {
    if (quote) {
      return (
        <Stack direction='row' mb={3} justifyContent='space-between' width='100%'>
          <StatusHeader
            title={`Quote# ${quote.reference}`}
            statusChip={
              mappedStatus && (
                <StatusChip
                  label={mappedStatus.display}
                  variant='filled'
                  bgColor={mappedStatus.color.bg}
                  textColor={mappedStatus.color.text}
                  data-testid={TestIds.components.statusHeader.statusBadge}
                />
              )
            }
            text={<QuoteExpiry quote={quote} />}
          />

          <Stack direction='column' justifyContent='space-between' spacing={2}>
            <QuoteCreatedAt createdAt={quote.createdAt} />
            {!isSm ? renderMoreOptions() : null}
            {isTfo && quote.status === QuoteStatus.Requested ? (
              <Button disabled={submitting} variant='contained' onClick={onSend}>
                Send
              </Button>
            ) : null}
          </Stack>
        </Stack>
      )
    }
  }

  const renderNoQuoteFound = useMemo(
    () => (
      <Box mb={2}>
        <YesNoAction
          display='Quote not available'
          body='We are unable to find this quote.'
          optionB={{
            label: 'Back to Quotes',
            onClick: () => {
              navigate('/quotes')
            },
          }}
        />
      </Box>
    ),
    [],
  )

  const twoColumnLayout = useMemo(() => {
    if (quote) return showQuoteAsTwoColumnLayout(quote, isTfo)
    return false
  }, [quote, account])

  const handleValidation = () => {
    const totalProducts = products.reduce((prev, curr) => {
      return prev + curr.quantity
    }, 0)
    return totalProducts > 0
  }

  const submitAcceptQuote = async ({ name, email, phone, reference }: HandleSubmitQuoteParams) => {
    try {
      setSubmitting(true)
      if (!quote?.version || !account?.accountNumber) return
      const result = await apiClient.post<SearchQuotesResponse, AcceptQuotePayload>(
        `/quotes/${quoteId}/accept`,
        {
          version: quote?.version,
          accountNumber: account?.accountNumber,
          products: products.map((p) => pick(p, ['quantity', 'productId'])),
          contact: {
            name,
            phone,
            email,
          },
          delivery: quote.delivery,
          customerReference: reference || null,
          note,
        },
      )
      if (result && result.status === 200 && result.data) {
        setIsSubmitQuoteModalOpen(false)
        navigate('/quote-order-submitted')
      } else {
        setError(true)
      }
    } catch (error) {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  const handleContinue = async () => {
    if (!account || !quote) return
    if (!handleValidation()) return
    setError(false)
    setIsSubmitQuoteModalOpen(true)
  }

  const handleSubmitQuote = (arg: HandleSubmitQuoteParams) => {
    setError(false)
    if (!handleValidation()) return
    submitAcceptQuote(arg)
  }

  return (
    <PageContainer
      data-testid={TestIds.pages.quoteDetails}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        maxWidth: `${CONTENT_LAYOUT_WIDTH}px`,
      }}
    >
      <BackButton text='All quotes' onClick={() => navigate('/quotes')} />
      {loading && <Loader />}
      {!loading && !isNoQuoteFound && quote && (
        <>
          {quote?.isLocked && quote.status === QuoteStatus.Offered ? (
            <Banner
              sx={{
                width: '100%',
                marginBottom: '16px',
                justifyContent: 'center',
              }}
              testId={TestIds.feat.quotes.details.quoteLocked}
              symbol='lock'
              title=''
              body='The only adjustment available for this quote is increasing the product quantity'
            />
          ) : null}
          {renderTitle()}

          <CartColumnView>
            {isSm ? renderMoreOptions() : null}
            <QuoteDetailsProducts
              quote={quote}
              originalProducts={originalProducts}
              products={products}
              setProducts={setProducts}
            />
            {twoColumnLayout ? (
              <FlexShrink>
                <QuoteSummary
                  quote={quote}
                  setQuote={setQuote}
                  handleContinue={handleContinue}
                  note={note}
                  setNote={setNote}
                >
                  <QuoteSummaryTotal
                    accountName={account?.accountName}
                    accountNumber={account?.accountNumber}
                    products={products}
                    originalProducts={originalProducts}
                    freightChargeExcGst={quote?.delivery?.freightChargeExcGst}
                  />
                </QuoteSummary>
              </FlexShrink>
            ) : null}
          </CartColumnView>
          <QuoteConfirmDetails
            quote={quote}
            error={error}
            isModalOpen={isSubmitQuoteModalOpen}
            setIsModalOpen={setIsSubmitQuoteModalOpen}
            submitting={submitting}
            handleSubmitQuote={handleSubmitQuote}
          />
        </>
      )}
      {!loading && isNoQuoteFound && renderNoQuoteFound}
      {isTfo && quote ? (
        <SendOfferModal
          isModalOpen={isSendOfferModalOpen}
          quote={quote}
          products={products}
          setIsModalOpen={setIsSendOfferModalOpen}
        />
      ) : null}
    </PageContainer>
  )
}

export default QuoteDetails
